import React, { useEffect, useMemo, useRef, useState } from "react";
import Button from "@common/components/Button";
import RightDrawer from "@common/components/RightDrawer";
import Typography from "@common/components/Typography";
import {
  Box,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTranslation } from "react-i18next";
import Item from "./item";
import { WithGrow } from "@common/components/WithGrow";
import { Backspace, DragHandle } from "@mui/icons-material";
import { intersection } from "lodash";
import DndProvider from "@common/components/DragNDrop";
import { useSpecificListPreferences } from "@common/components/SpecificPreferences/hooks";
import { useAutoScroll } from "@common/components/ListDetailViews/ListView/useAutoScroll";
import { useDragDropManager, useDragLayer } from "react-dnd";
import { ListDnDTypes } from "@common/components/ListDetailViews/ListView/const";

type DrawerProps = {
  onClose?: () => void;
  onReset?: () => void;
  onCancel?: () => void;
  open?: boolean;
  columns?: { key: string; label: string }[];
  disabled?: string[];
  selected?: string[];
  handleSelection?: (column: string, value: boolean) => void;
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
  handleReorder?: (key: number, position: number) => void;
};

const ColumnList = ({
  title,
  columns,
  selected,
  disabled,
  handleSelection,
  handleReorder,
  draggable,
  containerRef = null,
}: {
  title?: string;
  columns: {
    key: string;
    label: string;
  }[];
  selected: string[];
  disabled: string[];
  handleSelection: (key: string, value: boolean) => void;
  handleReorder?: (key: number, position: number) => void;
  draggable?: boolean;
  containerRef?: React.RefObject<HTMLElement | null> | null;
}) => {
  const updateItemPosition = useAutoScroll("vertical", containerRef);
  const dragDropManager = useDragDropManager();
  const monitor = dragDropManager.getMonitor();

  useEffect(() => {
    const unsubscribe = monitor.subscribeToOffsetChange(() => {
      const currentType = monitor.getItemType();
      if (currentType === ListDnDTypes.DRAWER_ORDER) {
        const offset = monitor.getClientOffset();
        updateItemPosition(offset);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [monitor, updateItemPosition]);
  const { t } = useTranslation();
  return (
    <>
      {title && (
        <Box>
          <Typography
            sx={{
              my: 1,
            }}
          >
            {t(title)}
          </Typography>
        </Box>
      )}

      {columns.map((column) => (
        <Item
          key={column.key}
          id={columns.findIndex((c) => c.key === column.key)}
          value={column.key}
          label={column.label}
          disabled={disabled.includes(column.key)}
          checked={selected.includes(column.key)}
          onChange={(value) => handleSelection(column.key, value)}
          draggable={draggable}
          columns={columns}
          handleReorder={handleReorder}
        />
      ))}
    </>
  );
};
const DragLayer = () => {
  const { item, currentOffset, isDragging } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    currentOffset: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging(),
  }));

  const getItemStyles = (currentOffset: { x: number; y: number } | null) => {
    if (!currentOffset) return { display: "none" } as const;

    const { x, y } = currentOffset;
    return {
      position: "fixed",
      width: "30rem",
      pointerEvents: "none",
      left: `${x}px`,
      top: `${y}px`,
      opacity: 0.8,
      zIndex: 9999,
    } as const;
  };

  if (!isDragging || !item) return null;

  return (
    <Box sx={getItemStyles(currentOffset)}>
      <Box
        sx={{
          bgcolor: ({ palette }) => `${palette.background.dark}`,
          my: 0,
          minHeight: "2.5rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Switch checked={true} />
          <Box component="span">{item.label}</Box>
        </Box>
        <IconButton
          aria-label="drag"
          size="medium"
          sx={{
            cursor: "grab",
          }}
        >
          <DragHandle fontSize="inherit" />
        </IconButton>
      </Box>
    </Box>
  );
};

const Drawer = (props: DrawerProps) => {
  const {
    onClose = (b: boolean) => {},
    onReset = () => {},
    handleSelection = () => {},
    onCancel = () => {},
    open = false,
    columns = [],
    selected = [],
    disabled = [],
    onSelectAll = () => {},
    onDeselectAll = () => {},
    handleReorder = () => {},
  } = props;
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const [{ columnOrder }, setSpecificListPreferences] =
    useSpecificListPreferences();

  const tabSelected = intersection(
    selected,
    columns.flatMap((x) => x.key)
  );

  const searched = useMemo(() => {
    const lowerCaseSearch = search.toLowerCase();
    return columns.filter(
      (column) =>
        column.key.toLowerCase().includes(lowerCaseSearch) ||
        column.label.toLowerCase().includes(lowerCaseSearch)
    );
  }, [search, columns]);

  const handleSave = () => {
    setSpecificListPreferences({
      columnOrder: selected,
    });
    onClose(true);
  };

  const deselectedColumns = useMemo(
    () =>
      columns
        .filter((column) => !selected.includes(column.key))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [columns, selected]
  );

  const selectedColumns = selected.flatMap((s) => {
    const column = columns.find((column) => column.key === s);
    return column ? [column] : [];
  });
  return (
    <RightDrawer
      open={open}
      onClose={handleSave}
      title={t("Show columns", { tabSelected: tabSelected.length })}
      closeButton={
        <Box
          sx={{
            flexDirection: "row",
            flexWrap: "nowrap",
            justifyContent: "flex-end",
            "& > * , .MuiButtonBase-root": {
              marginLeft: 1,
            },
          }}
        >
          <Button color="white" onClick={handleSave}>
            Save
          </Button>
          <Button color="greyed" variant="outlined" onClick={onReset}>
            <Typography variant="button">{t("Reset")}</Typography>
          </Button>
          <Button color="greyed" variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
        </Box>
      }
      bottomContent={
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "nowrap",
            justifyContent: "flex-end",
            "& > * , .MuiButtonBase-root": {
              marginLeft: 1,
            },
          }}
        >
          <Button
            variant="outlined"
            color="transparent"
            onClick={onDeselectAll}
          >
            Deselect all
          </Button>
          <Button variant="outlined" color="transparent" onClick={onSelectAll}>
            Select all
          </Button>
        </Box>
      }
    >
      <WithGrow
        direction={"column"}
        fixed={
          <Box sx={{ padding: 1 }}>
            <TextField
              label={t("Search")}
              value={search}
              onChange={(evt) => setSearch(evt.target.value)}
              fullWidth
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton color="inherit" onClick={() => setSearch("")}>
                        <Backspace />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        }
      >
        <Grid
          ref={containerRef}
          container
          direction="column"
          wrap="nowrap"
          sx={{
            height: 1,
            padding: 1,
            overflowY: "scroll",
            "& > *": { my: 1 },
          }}
        >
          <DndProvider>
            {search ? (
              <ColumnList
                columns={searched}
                selected={selected}
                disabled={disabled}
                handleSelection={handleSelection}
              />
            ) : (
              <>
                <DragLayer />
                <ColumnList
                  containerRef={containerRef}
                  title="Selected Columns"
                  columns={selectedColumns}
                  selected={selected}
                  disabled={disabled}
                  handleSelection={handleSelection}
                  handleReorder={handleReorder}
                  draggable={true}
                />
                {deselectedColumns.length > 0 && (
                  <ColumnList
                    title="All Columns"
                    columns={deselectedColumns}
                    selected={selected}
                    disabled={disabled}
                    handleSelection={handleSelection}
                  />
                )}
              </>
            )}
          </DndProvider>
        </Grid>
      </WithGrow>
    </RightDrawer>
  );
};

export default Drawer;
